import { useCallback, useEffect, useRef, useState } from "react";
import type { GameSnapshot, ServerMessage } from "../shared/protocol";

const PLAYER_TOKEN_KEY = "tee19-player-token";
const PLAYER_NAME_KEY = "tee19-player-name";
const TOKEN_PATTERN = /^[-a-zA-Z0-9]{16,64}$/;

export type ConnectionPhase = "connecting" | "connected" | "reconnecting" | "failed";

export interface GameConnection {
  connectionPhase: ConnectionPhase;
  errorMessage: string;
  myId: string | null;
  playerName: string;
  snapshot: GameSnapshot | null;
  getSnapshot: () => GameSnapshot | null;
  sendShot: (angle: number, power: number) => boolean;
  setPlayerName: (name: string) => void;
}

function readStoredValue(key: string): string {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}

function writeStoredValue(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage can be disabled. The current session still works without persistence.
  }
}

function playerToken(): string {
  const stored = readStoredValue(PLAYER_TOKEN_KEY);
  if (TOKEN_PATTERN.test(stored)) return stored;

  const generated =
    crypto.randomUUID?.() ??
    Array.from(crypto.getRandomValues(new Uint8Array(24)), (value) =>
      value.toString(16).padStart(2, "0"),
    ).join("");
  writeStoredValue(PLAYER_TOKEN_KEY, generated);
  return generated;
}

function isServerMessage(value: unknown): value is ServerMessage {
  if (!value || typeof value !== "object" || !("type" in value)) return false;
  return ["welcome", "state", "rejected", "error", "pong"].includes(
    String((value as { type: unknown }).type),
  );
}

function websocketUrl(): string {
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${location.host}/play`;
}

export function useGameConnection(): GameConnection {
  const [connectionPhase, setConnectionPhase] = useState<ConnectionPhase>("connecting");
  const [errorMessage, setErrorMessage] = useState("");
  const [myId, setMyId] = useState<string | null>(null);
  const [playerName, setPlayerNameState] = useState(() => readStoredValue(PLAYER_NAME_KEY));
  const [snapshot, setSnapshot] = useState<GameSnapshot | null>(null);
  const snapshotRef = useRef<GameSnapshot | null>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const nameRef = useRef(playerName);
  const tokenRef = useRef(playerToken());

  useEffect(() => {
    nameRef.current = playerName;
  }, [playerName]);

  useEffect(() => {
    if (!["http:", "https:"].includes(location.protocol)) {
      setConnectionPhase("failed");
      setErrorMessage("Öffne das Spiel über den Entwicklungsserver, nicht als einzelne Datei.");
      return;
    }

    let stopped = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let snapshotTimer: ReturnType<typeof setTimeout> | undefined;
    let retries = 0;

    const publishSnapshot = (): void => {
      snapshotTimer = undefined;
      setSnapshot(snapshotRef.current);
    };

    const scheduleReconnect = (): void => {
      if (stopped) return;
      setConnectionPhase("reconnecting");
      const delay = Math.min(8_000, 600 * 2 ** retries);
      retries += 1;
      retryTimer = setTimeout(() => void connect(), delay);
    };

    const connect = (): void => {
      if (stopped) return;
      setConnectionPhase(retries ? "reconnecting" : "connecting");
      setErrorMessage("");

      const socket = new WebSocket(websocketUrl());
      socketRef.current = socket;

      socket.addEventListener("open", () => {
        if (stopped || socket !== socketRef.current) return;
        retries = 0;
        socket.send(
          JSON.stringify({ type: "join", token: tokenRef.current, name: nameRef.current }),
        );
      });

      socket.addEventListener("message", (event) => {
        if (stopped || socket !== socketRef.current) return;
        let message: unknown;
        try {
          message = JSON.parse(String(event.data));
        } catch {
          return;
        }
        if (!isServerMessage(message)) return;

        if (message.type === "welcome") {
          setMyId(message.id);
          setConnectionPhase("connected");
          return;
        }
        if (message.type === "state") {
          const previous = snapshotRef.current;
          if (
            previous &&
            previous.room === message.room &&
            typeof message.revision === "number" &&
            message.revision <= previous.revision
          ) {
            return;
          }
          snapshotRef.current = message;
          if (
            !previous ||
            previous.phase !== message.phase ||
            previous.courseIndex !== message.courseIndex
          ) {
            if (snapshotTimer) clearTimeout(snapshotTimer);
            publishSnapshot();
          } else if (!snapshotTimer) {
            snapshotTimer = setTimeout(publishSnapshot, 100);
          }
          setConnectionPhase("connected");
          return;
        }
        if (message.type === "rejected" || message.type === "error") {
          setErrorMessage(message.message);
        }
      });

      socket.addEventListener("close", () => {
        if (stopped || socket !== socketRef.current) return;
        socketRef.current = null;
        scheduleReconnect();
      });
      socket.addEventListener("error", () => socket.close());
    };

    const handlePageShow = (event: PageTransitionEvent): void => {
      if (event.persisted && !socketRef.current) void connect();
    };

    window.addEventListener("pageshow", handlePageShow);
    void connect();

    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (snapshotTimer) clearTimeout(snapshotTimer);
      socketRef.current?.close(1000, "Page closed");
      socketRef.current = null;
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  const sendShot = useCallback((angle: number, power: number): boolean => {
    const socket = socketRef.current;
    if (socket?.readyState !== WebSocket.OPEN) return false;
    try {
      socket.send(JSON.stringify({ type: "shot", angle, power }));
      return true;
    } catch {
      return false;
    }
  }, []);

  const setPlayerName = useCallback((name: string): void => {
    const clean = Array.from(name.trim()).slice(0, 16).join("");
    if (!clean) return;
    nameRef.current = clean;
    setPlayerNameState(clean);
    writeStoredValue(PLAYER_NAME_KEY, clean);

    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: "name", name: clean }));
    }
  }, []);
  const getSnapshot = useCallback((): GameSnapshot | null => snapshotRef.current, []);

  return {
    connectionPhase,
    errorMessage,
    myId,
    playerName,
    snapshot,
    getSnapshot,
    sendShot,
    setPlayerName,
  };
}
